/**
 * Coach schedule. Ported from the original app's CoachScheduleView.tsx —
 * same UI verbatim, using the api client instead of raw fetch.
 * Reads/writes GET/POST/DELETE /api/coach/schedule.
 */

import { useEffect, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  X,
  Video,
  MapPin,
  Trash2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { CoachWeekView, clientName } from '@/components/CoachWeekView';

type Tab = 'calendar' | 'availability';

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface AvailabilitySlot {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

interface AppointmentEntry {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  appointment_type: string;
  status: string;
  notes: string | null;
  users: { first_name: string; last_name: string; email: string } | null;
}

interface ScheduleData {
  slots: AvailabilitySlot[];
  appointments: AppointmentEntry[];
}

export default function CoachSchedule() {
  const [tab, setTab] = useState<Tab>('calendar');
  // Calendar tab: the coach's week at a glance by default; month grid on demand.
  const [calView, setCalView] = useState<'week' | 'month'>('week');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [appointments, setAppointments] = useState<AppointmentEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlotDay, setNewSlotDay] = useState(1);
  const [newSlotStart, setNewSlotStart] = useState('09:00');
  const [newSlotEnd, setNewSlotEnd] = useState('10:00');
  const [saving, setSaving] = useState(false);

  const monthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthStr]);

  async function loadData() {
    setLoading(true);
    try {
      const data = await api.get<ScheduleData>(`/api/coach/schedule?month=${monthStr}`);
      setSlots(data.slots || []);
      setAppointments(data.appointments || []);
    } catch {
      /* */
    } finally {
      setLoading(false);
    }
  }

  async function addSlot() {
    setSaving(true);
    try {
      await api.post('/api/coach/schedule', {
        day_of_week: newSlotDay,
        start_time: newSlotStart,
        end_time: newSlotEnd,
      });
      setShowAddSlot(false);
      await loadData();
    } catch {
      /* */
    } finally {
      setSaving(false);
    }
  }

  async function removeSlot(id: string) {
    try {
      await api.del(`/api/coach/schedule?id=${id}`);
      setSlots((prev) => prev.filter((s) => s.id !== id));
    } catch {
      /* */
    }
  }

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfWeek = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const monthLabel = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const selectedAppointments = appointments.filter((a) => a.date === selectedDate);
  const todayStr = new Date().toISOString().split('T')[0];
  const todaySessions = appointments.filter((a) => a.date === todayStr).length;

  return (
    <div className="px-5">
      {/* Tab Switcher */}
      <div className="flex gap-1.5 p-1 rounded-lg mb-5" style={{ background: 'var(--surface)' }}>
        {(['calendar', 'availability'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="flex-1 py-2 text-sm font-medium rounded-md capitalize transition-all"
            style={{ background: tab === t ? 'var(--gold)' : 'transparent', color: tab === t ? 'var(--navy)' : 'var(--gray-400)' }}
          >
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse h-20" style={{ background: 'var(--surface)' }} />
          ))}
        </div>
      ) : tab === 'calendar' ? (
        <div className="flex flex-col gap-4">
          <div className="flex gap-1 p-1 rounded-lg self-start" style={{ background: 'var(--surface)' }}>
            {(['week', 'month'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setCalView(v)}
                className="px-4 py-1.5 text-xs font-medium rounded-md transition-all"
                style={{ background: calView === v ? 'var(--gold)' : 'transparent', color: calView === v ? 'var(--navy)' : 'var(--gray-400)' }}
              >
                {v === 'week' ? 'Week' : 'Month'}
              </button>
            ))}
          </div>
          {calView === 'week' ? <CoachWeekView /> : (<>
          <div className="card text-center">
            <p className="text-xs" style={{ color: 'var(--gray-500)' }}>Today&apos;s Sessions</p>
            <p className="text-3xl mt-1" style={{ fontFamily: "var(--font-display), Georgia, serif", lineHeight: 1.05, color: "var(--cream)" }}>{todaySessions}</p>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} style={{ color: 'var(--gray-400)' }}>
                <ChevronLeft size={20} />
              </button>
              <p className="text-sm font-semibold text-white">{monthLabel}</p>
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} style={{ color: 'var(--gray-400)' }}>
                <ChevronRight size={20} />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <p key={i} className="text-xs py-1" style={{ color: 'var(--gray-500)' }}>{d}</p>
              ))}
              {Array.from({ length: firstDayOfWeek }, (_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isSelected = dateStr === selectedDate;
                const hasSession = appointments.some((a) => a.date === dateStr);
                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDate(dateStr)}
                    className="relative w-9 h-9 mx-auto rounded-full flex items-center justify-center text-sm transition-all"
                    style={{
                      background: isSelected ? 'var(--gold)' : 'transparent',
                      color: isSelected ? 'var(--navy)' : 'var(--gray-200)',
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    {day}
                    {!isSelected && hasSession && (
                      <div className="absolute bottom-0.5 w-1 h-1 rounded-full" style={{ background: 'var(--success)' }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sessions for selected date */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--gray-400)' }}>
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
            {selectedAppointments.length === 0 ? (
              <div className="card text-center py-6">
                <p className="text-sm" style={{ color: 'var(--gray-400)' }}>No sessions on this date</p>
              </div>
            ) : (
              selectedAppointments.map((appt) => {
                const client = appt.users;
                return (
                  <div key={appt.id} className="card mb-3">
                    <div className="flex items-start gap-3">
                      {appt.appointment_type === 'virtual' ? (
                        <Video size={18} style={{ color: 'var(--info)' }} className="mt-0.5" />
                      ) : (
                        <MapPin size={18} style={{ color: 'var(--success)' }} className="mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">
                          {formatTime(appt.start_time)} - {formatTime(appt.end_time)}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--gray-400)' }}>
                          {clientName(client)}
                        </p>
                        {appt.notes && (
                          <p className="text-xs mt-1" style={{ color: 'var(--gray-500)' }}>{appt.notes}</p>
                        )}
                      </div>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          background: appt.status === 'upcoming' ? 'rgba(34,197,94,0.12)' : 'rgba(107,114,128,0.12)',
                          color: appt.status === 'upcoming' ? 'var(--success)' : 'var(--gray-400)',
                        }}
                      >
                        {appt.status}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          </>)}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>Working Hours</p>
            <button onClick={() => setShowAddSlot(true)} className="flex items-center gap-1 text-xs" style={{ color: 'var(--gold)' }}>
              <Plus size={12} /> Add Slot
            </button>
          </div>

          {showAddSlot && (
            <div className="card" style={{ border: '1px solid var(--gold-muted)' }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-white">New Availability Slot</p>
                <button onClick={() => setShowAddSlot(false)} style={{ color: 'var(--gray-400)' }}><X size={16} /></button>
              </div>
              <div className="flex flex-col gap-3">
                <div>
                  <label className="label">Day</label>
                  <select className="input" value={newSlotDay} onChange={(e) => setNewSlotDay(parseInt(e.target.value))}>
                    {daysOfWeek.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Start</label>
                    <input type="time" className="input" value={newSlotStart} onChange={(e) => setNewSlotStart(e.target.value)} />
                  </div>
                  <div>
                    <label className="label">End</label>
                    <input type="time" className="input" value={newSlotEnd} onChange={(e) => setNewSlotEnd(e.target.value)} />
                  </div>
                </div>
                <button onClick={addSlot} disabled={saving} className="btn-gold text-sm py-2">
                  {saving ? 'Saving...' : 'Add Slot'}
                </button>
              </div>
            </div>
          )}

          {daysOfWeek.map((day, dayIndex) => {
            const daySlots = slots.filter((s) => s.day_of_week === dayIndex);
            return (
              <div key={day} className="card">
                <p className="text-sm font-medium text-white mb-1">{day}</p>
                {daySlots.length === 0 ? (
                  <div className="flex items-center gap-2">
                    <Clock size={12} style={{ color: 'var(--gray-500)' }} />
                    <span className="text-xs" style={{ color: 'var(--gray-400)' }}>Not set</span>
                  </div>
                ) : (
                  daySlots.map((slot) => (
                    <div key={slot.id} className="flex items-center justify-between py-1">
                      <span className="text-xs" style={{ color: 'var(--gray-300)' }}>
                        {slot.start_time} - {slot.end_time}
                      </span>
                      <button onClick={() => removeSlot(slot.id)} style={{ color: 'var(--error)' }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatTime(time: string): string {
  const [h, m] = time.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${ampm}`;
}
